import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService } from './index3.js';
import { b as breakpoints, r as remCalc } from './index5.js';
import { a as ButtonAppearance, d as defineCustomElement$5 } from './roche-button2.js';
import { a as RocheGenericLinkIconPosition, d as defineCustomElement$4 } from './roche-generic-link2.js';
import { T as Transparency } from './roche-header.types.js';
import { A as AdobeTracking } from './index7.js';
import { d as defineCustomElement$3 } from './roche-icon2.js';
import { d as defineCustomElement$2 } from './roche-offcanvas-trigger2.js';
import { t as throttle } from './throttle.js';

const rocheHeaderCss = ":host{display:block;margin-top:3rem;margin-top:0;z-index:300;display:flex;justify-content:center;position:sticky;top:0;width:100%;background-color:#fff;user-select:none;transition-property:transform, background-color;transition-timing-function:cubic-bezier(0.23, 0, 0.56, 0.99);transition-duration:250ms;}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}:host ::slotted(roche-richtext){padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:400;font-size:1.125rem;line-height:1.6875rem;display:none !important;font-weight:400 !important}:host ::slotted(roche-richtext) strong{font-weight:600}@media (min-width: 48rem){:host ::slotted(roche-richtext){font-size:1.125rem;line-height:1.8125rem}}@media (min-width: 90rem){:host ::slotted(roche-richtext){font-size:1.25rem;line-height:1.875rem}}@media (min-width: 48rem){:host ::slotted(roche-richtext){display:block !important;margin:0 !important;color:#fff !important}}@media print{:host{border-bottom:none !important;box-shadow:none !important;display:block !important;position:absolute !important;left:0;top:0;transform:translateY(100%) !important}}.print-logo{display:none}@media print{.print-logo{display:block;position:absolute;left:0;top:0;padding-left:1.25rem}}.print-logo roche-icon{fill:var(--color-interactive--primary-idle)}.content-wrapper{display:flex;flex-direction:column;justify-content:space-between;width:100%;height:64px;max-width:calc(100% - 40px)}@media print{.content-wrapper{display:none !important}}@media (min-width: 48rem){.content-wrapper{height:88px;max-width:calc(100% - 64px)}}@media (min-width: 64rem){.content-wrapper{height:160px;max-width:calc(100% - 128px)}}@media (min-width: 90rem){.content-wrapper{height:160px;max-width:calc(100% - 192px)}}@media (min-width: 104.5rem){.content-wrapper{max-width:1552px}}.header-container{display:flex;align-items:center;height:100%;}@media (min-width: 64rem){.header-container{align-items:flex-start;height:auto}}.header-container .logo{display:block;margin-right:auto;padding-top:0}@media (min-width: 64rem){.header-container .logo{padding-top:2.5rem}}.header-container .logo roche-icon{fill:var(--color-interactive--primary-idle)}.header-container .logo .imageLogo{padding-top:0;width:auto}.header-container .search{margin:-0.125rem 0 0 1rem;padding-top:0;color:#544f4f;cursor:pointer;transition:border-color 100ms ease-in;position:relative}@media (min-width: 64rem){.header-container .search{display:none}}.header-container .search roche-icon{border-bottom:0.125rem solid transparent}.header-container .search roche-icon:hover{fill:var(--color-interactive--primary-idle);border-bottom:0.125rem solid var(--color-interactive--primary-idle)}.header-container .current-page>roche-icon{fill:var(--color-interactive--primary-idle)}.header-container .menu{display:block;color:#544f4f;fill:#544f4f;margin-left:1.5rem}@media (min-width: 64rem){.header-container .menu{display:none}}slot[name=primary],slot[name=secondary]{display:none}@media (min-width: 64rem){slot[name=primary],slot[name=secondary]{display:block}}::slotted([slot=secondary]){padding-top:2.8125rem}:host(.scrolled){box-shadow:inset 0 -0.0625rem 0 #dbd6d1}:host(.hidden){transform:translateY(-100%);transition-duration:300ms}:host(.transparent-background){background-color:transparent}:host(.fixed){position:fixed}:host(.microsite){--color-interactive--primary-idle:#fff;--color-interactive--primary-hover:rgba(255, 255, 255, 0.7);position:fixed;background-color:transparent;border-bottom:1px solid #eae8e5}:host(.microsite)::before{z-index:-50;content:\"\";position:absolute;top:0;left:0;width:100%;height:7.5rem;background-image:linear-gradient(to bottom, rgba(0, 0, 0, 0.65) 0, rgba(0, 0, 0, 0.2) 60%, rgba(0, 0, 0, 0) 100%);pointer-events:none}:host(.microsite) .content-wrapper{padding:1.25rem 0;min-height:4.6875rem;height:auto}:host(.microsite) .header-container{align-items:center;justify-content:space-between}@media (min-width: 48rem){:host(.microsite) .header-container{justify-content:center}}@media (min-width: 48rem){:host(.microsite) .logo-container,:host(.microsite) .title-container,:host(.microsite) .buttons-container{flex:1}}:host(.microsite) .logo-container{display:flex;text-align:left}:host(.microsite) .title-container{text-align:center}:host(.microsite) .buttons-container{text-align:right}:host(.microsite) .logo{z-index:50;display:inline-block;padding-top:0;margin:0}:host(.microsite) roche-button{display:none}@media (min-width: 48rem){:host(.microsite) roche-button{display:inline-block;margin:0;border-color:var(--color-interactive--primary-idle)}}:host(.microsite) roche-generic-link{display:block;margin:0}@media (min-width: 48rem){:host(.microsite) roche-generic-link{display:none}}:host([dir=rtl].microsite) .buttons-container{text-align:left}:host(.microsite.hidden)::before{height:6.25rem}:host(.microsite.scrolled){box-shadow:none}:host([dir=rtl]) .header-container .menu{margin-right:1.5rem;margin-left:0}:host([dir=rtl]) .header-container .search{margin-right:1rem;margin-left:0}:host([dir=rtl]) .header-container .logo{margin-left:auto;margin-right:0}";
const RocheHeaderStyle0 = rocheHeaderCss;

const SCROLL_THRESHOLD = 100;
const RocheHeader$1 = /*@__PURE__*/ proxyCustomElement(class RocheHeader extends HTMLElement {
    handleScroll() {
        this.previousScrollPosition = this.scrollPosition;
        this.scrollPosition = window.scrollY;
    }
    async forceHide() {
        this.previousScrollPosition = window.scrollY;
        this.scrollPosition = this.previousScrollPosition + 1;
    }
    shouldHeaderBeTransparent() {
        return (this.transparency === Transparency.ALWAYS
            || (this.transparency === Transparency.AT_TOP && this.scrollPosition < 1));
    }
    shouldHeaderHide() {
        // Never hide a fixed header
        if (this.fixed) {
            return false;
        }
        // Do not hide the header in the vicinity of the top of the page
        if (this.scrollPosition < SCROLL_THRESHOLD) {
            return false;
        }
        // Hide the header below a certain distance from the top when scrolling down
        if (this.scrollPosition >= this.previousScrollPosition) {
            return true;
        }
        // Hide the header in the vicinity of the end of the page.
        // This is used to prevent Safari's elastic scrolling from revealing the header.
        // TODO: Use https://caniuse.com/css-overscroll-behavior once Safari supports it.
        if (Math.abs(this.scrollPosition
            + document.documentElement.clientHeight
            - document.documentElement.scrollHeight) < SCROLL_THRESHOLD) {
            return true;
        }
        // In all other cases do not hide the header
        return false;
    }
    // eslint-disable-next-line class-methods-use-this
    get logoHeight() {
        return window.innerWidth >= breakpoints.medium ? 40 : 30;
    }
    // eslint-disable-next-line class-methods-use-this
    hasDefaultLogo() {
        var _a;
        return !DomService.getGlobalConfig().headerIconUrl || ((_a = DomService.getGlobalConfig().headerIconUrl) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === 'default';
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.previousScrollPosition = window.scrollY;
        this.homeUrl = '/';
        this.menuId = 'roche-offcanvas-menu';
        this.searchId = 'roche-offcanvas-search';
        this.transparency = Transparency.NEVER;
        this.fixed = false;
        this.globalSearchLink = undefined;
        this.isMicrosite = false;
        this.scrollPosition = window.scrollY;
        this.handleScroll = throttle(this.handleScroll.bind(this), 50);
        this.shouldHeaderHide = this.shouldHeaderHide.bind(this);
    }
    // eslint-disable-next-line class-methods-use-this
    componentDidLoad() {
        window.dispatchEvent(new CustomEvent('rocheHeaderLoaded'));
        DomService.handleTextDirection(this.element);
    }
    renderDefaultHeader() {
        const headerClasses = {
            'transparent-background': this.shouldHeaderBeTransparent(),
            fixed: this.fixed,
            scrolled: this.scrollPosition > 0,
            hidden: this.shouldHeaderHide(),
        };
        const searchClasses = {
            search: true,
            'current-page': this.isSearchPage(),
        };
        return (h(Host, { class: headerClasses }, h("div", { class: "print-logo" }, this.getIcon(25)), h("div", { class: "content-wrapper" }, h("div", { class: "header-container" }, h("a", { class: "logo", href: this.homeUrl || '/' }, this.getIcon(this.logoHeight)), h("slot", { name: "secondary" }), this.globalSearchLink
            && h("a", { class: searchClasses, href: this.globalSearchLink, onClick: () => this.trackLinkClick() }, h("roche-icon", { class: "header__link-icon", icon: "search-mobile", height: `${remCalc(32)}rem` })), h("roche-offcanvas-trigger", { class: "menu", "offcanvas-id": this.menuId, "tracking-event-name": "navigation_offcanvas_click" }, h("roche-icon", { class: "icon", icon: "mobile-menu", height: `${remCalc(32)}rem` }))), h("slot", { name: "primary" }))));
    }
    renderMicrositeHeader() {
        const headerClasses = {
            scrolled: this.scrollPosition > 0,
            hidden: this.shouldHeaderHide(),
            microsite: this.isMicrosite,
        };
        return (h(Host, { class: headerClasses }, h("div", { class: "content-wrapper" }, h("div", { class: "header-container" }, h("div", { class: "logo-container" }, h("a", { class: "logo", href: this.homeUrl || '/' }, this.getIcon(this.logoHeight))), h("div", { class: "title-container" }, h("slot", { name: "microsite-title" })), h("div", { class: "buttons-container" }, h("roche-generic-link", { text: 'Go to roche.com', icon: 'arrow-right', iconPosition: RocheGenericLinkIconPosition.BEFORE, url: this.homeUrl || '/' }), h("roche-button", { text: 'Go to roche.com', appearance: ButtonAppearance.SECONDARY, href: this.homeUrl || '/' }))))));
    }
    render() {
        if (this.isMicrosite) {
            return this.renderMicrositeHeader();
        }
        return this.renderDefaultHeader();
    }
    isSearchPage() {
        var _a;
        if (typeof window === 'undefined') {
            return false;
        }
        return ((_a = window.location) === null || _a === void 0 ? void 0 : _a.pathname) === this.globalSearchLink;
    }
    getIcon(height) {
        if (this.hasDefaultLogo()) {
            return h("roche-icon", { class: "icon", icon: "logo", height: `${remCalc(height)}rem` });
        }
        if (StringService.getExtension(DomService.getGlobalConfig().headerIconUrl) === 'svg') {
            return h("object", { data: DomService.getGlobalConfig().headerIconUrl, height: height });
        }
        const styles = {
            height: `${remCalc(height)}rem`,
        };
        return h("img", { style: styles, src: DomService.getGlobalConfig().headerIconUrl, class: "imageLogo", loading: "lazy" });
    }
    get adobeTracking() {
        return {
            innerText: '',
            url: this.globalSearchLink,
            position: 'Header',
            type: 'Link',
            socialMedia: '',
            socialInteraction: '',
        };
    }
    trackLinkClick() {
        AdobeTracking(this.adobeTracking, 'defaultLinkClick');
    }
    get element() { return this; }
    static get style() { return RocheHeaderStyle0; }
}, [1, "roche-header", {
        "homeUrl": [1, "home-url"],
        "menuId": [1, "menu-id"],
        "searchId": [1, "search-id"],
        "transparency": [1],
        "fixed": [4],
        "globalSearchLink": [1, "global-search-link"],
        "isMicrosite": [4, "is-microsite"],
        "scrollPosition": [32],
        "forceHide": [64]
    }, [[9, "scroll", "handleScroll"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-header", "roche-button", "roche-generic-link", "roche-icon", "roche-offcanvas-trigger"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-header":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheHeader$1);
            }
            break;
        case "roche-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-generic-link":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-offcanvas-trigger":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheHeader = RocheHeader$1;
const defineCustomElement = defineCustomElement$1;

export { RocheHeader, defineCustomElement };

//# sourceMappingURL=roche-header.js.map